import { useCallback, useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

interface Source { slug: string; name: string; url: string; audience: "internal" | "public" }

/** Live values pages can show through a cortex-data block. */
export default function DataSourcesPanel() {
  const [sources, setSources] = useState<Source[]>([]);
  const [form, setForm] = useState({ slug: "", name: "", url: "", authToken: "", audience: "internal" });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const r = await authFetch("/api/admin/data-sources");
    if (r.ok) setSources((await r.json()).sources);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError("");
    const r = await authFetch("/api/admin/data-sources", {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, authToken: form.authToken || undefined }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setError(typeof d.detail === "string" ? d.detail : "Check the fields and try again."); return; }
    setForm({ slug: "", name: "", url: "", authToken: "", audience: "internal" }); load();
  };
  const remove = async (slug: string) => {
    await authFetch(`/api/admin/data-sources/${slug}`, { method: "DELETE" });
    load();
  };
  const field = "px-2.5 py-1.5 text-[12px] border border-slate-200 rounded-lg w-full";

  return (
    <section aria-labelledby="data-sources-title" className="pt-6 border-t border-slate-100">
      <h3 id="data-sources-title" className="text-[14px] font-semibold text-slate-800">Data sources</h3>
      <p className="text-[12px] text-slate-500 mt-1 mb-3">Live values a page can show through a <code>cortex-data</code> block. Internal sources are shown to Joveo staff only; everyone else sees the page's fallback text.</p>
      <ul className="text-[12px] space-y-1.5 mb-3">
        {sources.map((s) => (
          <li key={s.slug} className="flex items-center justify-between gap-3">
            <span><span className="font-mono">{s.slug}</span> · {s.name} · {s.audience === "public" ? "Everyone" : "Joveo staff"}</span>
            <button type="button" onClick={() => remove(s.slug)} className="shrink-0 text-slate-500 hover:text-red-600">Remove</button>
          </li>
        ))}
      </ul>
      <form className="grid sm:grid-cols-2 gap-2" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <input aria-label="Slug" placeholder="feature-status" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={field} />
        <input aria-label="Name" placeholder="Feature status" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
        <input aria-label="URL" type="url" placeholder="https://example.com/data" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={field} />
        <input aria-label="Bearer token (optional)" type="password" autoComplete="off" placeholder="Bearer token (optional)" value={form.authToken} onChange={(e) => setForm({ ...form, authToken: e.target.value })} className={field} />
        <select aria-label="Who sees live values" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={field}>
          <option value="internal">Joveo staff only</option>
          <option value="public">Everyone who can open the page</option>
        </select>
        <button type="submit" disabled={!form.slug || !form.name || !form.url} className="px-3 py-1.5 text-[12px] font-medium rounded-lg bg-slate-800 text-white disabled:opacity-40">Save source</button>
      </form>
      {error && <p role="alert" className="text-[12px] text-red-600 mt-2">{error}</p>}
    </section>
  );
}
